import * as THREE from 'three';
import type { LimbRig } from './character-rig.js';
import { solveLimb } from './character-motion.js';

/** Actual handle radii in human-model.ts; the sling's loop and knot are pinched together. */
export function weaponGripRadius(part: string) {
  return part === 'shortbow' ? .019 : part === 'torch' ? .018 : part === 'javelin' ? .015 : part === 'sling' ? .008 : .0165;
}

/** Across the finger roots, touching the palm's front surface, in the wrist's frame. */
export function weaponGripMount(side: number, scale: number, radius: number) {
  return new THREE.Vector3(-side * (radius + .014 * scale), -.077 * scale, 0);
}

const grips = new WeakMap<LimbRig, Map<number, { fingers: number[][]; thumb: THREE.Quaternion[] }>>();

/** Wrap each digit around this handle rather than forcing every hand into the same fist. */
export function poseWeaponGrip(arm: LimbRig, grip: number, scale: number, radius: number) {
  let cached = grips.get(arm);
  if (!cached) { cached = new Map(); grips.set(arm, cached); }
  let pose = cached.get(radius);
  if (!pose) {
    const centre = new THREE.Vector2(-.077 * scale, radius + .014 * scale);
    const root = new THREE.Vector2(-.09 * scale, .006), around = root.distanceTo(centre);
    const fingers = [.036, .04, .037, .029].map(length => {
      let angle = Math.atan2(root.y - centre.y, root.x - centre.x), previous = root.clone(), turn = 0;
      return [.7, .7, .62].map(share => {
        angle -= 2 * Math.asin(Math.min(.98, length * scale * share / (2 * around)));
        const next = centre.clone().add(new THREE.Vector2(Math.cos(angle), Math.sin(angle)).multiplyScalar(around));
        const direction = next.clone().sub(previous), absolute = Math.atan2(-direction.y, -direction.x);
        const local = Math.atan2(Math.sin(absolute - turn), Math.cos(absolute - turn));
        previous = next; turn = absolute;
        return local;
      });
    });
    const thumb: THREE.Quaternion[] = [];
    if (arm.thumb) {
      const upper = new THREE.Object3D(), lower = new THREE.Object3D(), tip = new THREE.Object3D();
      upper.position.copy(arm.thumb[0].position); lower.position.copy(arm.thumb[1].position); tip.position.y = -.026 * scale;
      solveLimb({ upper, lower, hand: tip }, new THREE.Vector3((arm.side ?? 1) * .017 * scale, -.058 * scale, centre.y + radius + .007 * scale), new THREE.Vector3((arm.side ?? 1) * .5, 0, 1));
      thumb.push(upper.quaternion, lower.quaternion);
    }
    pose = { fingers, thumb }; cached.set(radius, pose);
  }
  for (const [finger, segments] of (arm.fingers ?? []).entries()) segments.forEach((segment, index) => {
    segment.rotation.x = THREE.MathUtils.lerp(index ? -.5 : -.22, pose.fingers[finger][index], grip);
  });
  if (arm.thumb) {
    const side = arm.side ?? 1;
    arm.thumb[0].rotation.set(-.35, 0, side * .62); arm.thumb[1].rotation.set(-.5, 0, 0);
    arm.thumb.forEach((segment, index) => segment.quaternion.slerp(pose.thumb[index], grip));
  }
}
